import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, PanInfo, useDragControls } from 'framer-motion';
import { Checkbox, Button, IconButton } from '@mui/material';
import { Add, Remove, Star, AccessTime } from '@mui/icons-material';
import { FoodItem } from '../types';
import { colors, borderRadius, spacing, typography, shadows } from '../styles/theme';

const FoodImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
`;

const DragHandle = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing.sm};
  cursor: grab;
  touch-action: none;
  user-select: none;
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover::after {
    background: ${colors.primary};
    transform: scaleY(1.2);
  }

  &:active::after {
    background: ${colors.primary};
    transform: scaleY(1.5);
  }

  &::after {
    content: '';
    width: 40px;
    height: 4px;
    background: ${colors.border};
    border-radius: 2px;
    transition: all 0.2s ease;
  }
`;

const Content = styled.div`
  padding: ${spacing.lg};
  max-height: calc(90vh - 200px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  /* Скрываем scrollbar на webkit браузерах */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Для Firefox */
  scrollbar-width: none;
`;

const Title = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xxl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.sm} 0;
`;

const Description = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.relaxed};
  margin: 0 0 ${spacing.lg} 0;
`;

const RatingTime = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
`;

const Time = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
`;

const Price = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xxxl};
  font-weight: ${typography.fontWeight.extraBold};
  color: ${colors.primary};
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.textPrimary};
  margin: ${spacing.lg} 0 ${spacing.md} 0;

  &:first-of-type {
    margin-top: 0;
  }
`;

const IngredientItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} 0;
  border-bottom: 1px solid ${colors.borderLight};

  &:last-child {
    border-bottom: none;
  }
`;

const IngredientInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex: 1;
`;

const IngredientName = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  color: ${colors.textPrimary};
`;

const IngredientPrice = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary};
  font-weight: ${typography.fontWeight.medium};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const QuantityButton = styled(IconButton)`
  background: ${colors.backgroundTertiary};
  color: ${colors.textPrimary};
  width: 32px;
  height: 32px;

  &:hover {
    background: ${colors.primary};
    color: ${colors.textInverse};
  }
`;

const Quantity = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.medium};
  min-width: 24px;
  text-align: center;
`;

const AddToCartButton = styled(Button)`
  width: 100%;
  padding: ${spacing.md};
  background: ${colors.primary};
  color: ${colors.textInverse};
  border-radius: ${borderRadius.lg};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  margin-top: ${spacing.lg};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

interface FoodDetailsDrawerProps {
  foodItem: FoodItem;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: FoodItem, selectedIngredients: string[], quantity: number) => void;
}

const FoodDetailsDrawer: React.FC<FoodDetailsDrawerProps> = ({
  foodItem,
  isOpen,
  onClose,
  onAddToCart
}) => {
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>(
    foodItem.ingredients.filter(ing => ing.isDefault).map(ing => ing.id)
  );
  const [quantity, setQuantity] = useState(1);
  const dragControls = useDragControls();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Блокируем скролл body когда шторка открыта
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  const handleIngredientToggle = (ingredientId: string) => {
    setSelectedIngredients(prev =>
      prev.includes(ingredientId)
        ? prev.filter(id => id !== ingredientId)
        : [...prev, ingredientId]
    );
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const calculateTotalPrice = () => {
    const basePrice = foodItem.price;
    const extraIngredients = foodItem.ingredients.filter(
      ing => selectedIngredients.includes(ing.id) && !ing.isDefault
    );
    const extraPrice = extraIngredients.reduce((sum, ing) => sum + ing.price, 0);
    return (basePrice + extraPrice) * quantity;
  };

  const handleAddToCart = () => {
    onAddToCart(foodItem, selectedIngredients, quantity);
    onClose();
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    const { offset, velocity } = info;

    // Закрываем шторку если потянули вниз достаточно сильно или быстро
    if (offset.y > 100 || (offset.y > 50 && velocity.y > 500)) {
      onClose();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Закрываем только если кликнули именно на фоне, а не на шторке
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          touchAction: 'none',
        }}
        onClick={handleBackgroundClick}
      >
        <motion.div
          ref={drawerRef}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          drag="y"
          dragControls={dragControls}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
            style={{
              background: colors.backgroundSecondary,
              borderRadius: `${borderRadius.xl} ${borderRadius.xl} 0 0`,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              touchAction: 'none',
            }}
        >
          <FoodImage src={foodItem.image} alt={foodItem.name} />

          <DragHandle
            onPointerDown={(e) => {
              e.preventDefault();
              dragControls.start(e);
            }}
          />

          <Content>
            <Title>{foodItem.name}</Title>
            <Description>{foodItem.description}</Description>

            <RatingTime>
              <Rating>
                <Star sx={{ color: '#FFC107', fontSize: 16 }} />
                {foodItem.rating}
              </Rating>
              <Time>
                <AccessTime sx={{ fontSize: 16 }} />
                {foodItem.preparationTime} мин
              </Time>
            </RatingTime>

            <Price>{calculateTotalPrice()}₽</Price>

            <SectionTitle>Модификаторы</SectionTitle>

            {foodItem.ingredients.map((ingredient) => (
              <IngredientItem key={ingredient.id}>
                <IngredientInfo>
                  <Checkbox
                    checked={selectedIngredients.includes(ingredient.id)}
                    onChange={() => handleIngredientToggle(ingredient.id)}
                    sx={{
                      color: colors.primary,
                      '&.Mui-checked': {
                        color: colors.primary,
                      },
                    }}
                  />
                  <IngredientName>{ingredient.name}</IngredientName>
                </IngredientInfo>
                {ingredient.price > 0 && (
                  <IngredientPrice>+{ingredient.price}₽</IngredientPrice>
                )}
              </IngredientItem>
            ))}

            <SectionTitle>Количество</SectionTitle>

            <QuantityControls>
              <QuantityButton onClick={() => handleQuantityChange(-1)}>
                <Remove />
              </QuantityButton>
              <Quantity>{quantity}</Quantity>
              <QuantityButton onClick={() => handleQuantityChange(1)}>
                <Add />
              </QuantityButton>
            </QuantityControls>

            <AddToCartButton onClick={handleAddToCart}>
              Добавить в корзину • {calculateTotalPrice()}₽
            </AddToCartButton>
          </Content>
        </motion.div>
      </motion.div>
    </>
  );
};

export default FoodDetailsDrawer;
